import * as React from 'react'
import styled from '@emotion/styled/macro'
import TagManager from 'react-gtm-module'

import { useAICustomization } from '~/hooks/useAICustomization'
import { useAICustomizationPanel } from '~/hooks/useAICustomizationPanel'
import type { Product } from '~/types/Product'
import type { Color } from '~/types/Color'
import type { Design } from '~/types/Design'
import type { Customization } from '~/types/Customization'
import { useToast } from '~/hooks/useToast'

import { PromptInputSection } from '~/components/organisms/AICustomization/PromptInputSection'
import { ColorSelectionSection } from '~/components/organisms/AICustomization/ColorSelectionSection'
import { AICustomizationSidebarFooter } from './AICustomizationSidebarFooter'

interface Props {
  opened: boolean
  onClose: () => void
  onApply: (customization: Customization) => void
  onGeneratingChange?: (isGenerating: boolean) => void
  onTrackingAIUsage?: () => void
  onProceedToEngraving?: () => void
  product: Product
  colors: readonly Color[]
  design: Design
  currentCustomization: Customization
  promptExternal?: string
  onPromptExternalChange?: (v: string) => void
  selectedColorIdsExternal?: Set<string>
  onToggleColorExternal?: (id: string) => void
  onResetSelectionExternal?: () => void
  hasGeneratedOnce?: boolean
  onHasGeneratedOnceChange?: (v: boolean) => void
}

export const AICustomizationSidebar: React.FC<Props> = ({
  opened,
  onClose,
  onApply,
  onGeneratingChange,
  onTrackingAIUsage,
  onProceedToEngraving,
  product,
  colors,
  design,
  currentCustomization,
  promptExternal,
  onPromptExternalChange,
  selectedColorIdsExternal,
  onToggleColorExternal,
  onResetSelectionExternal,
  hasGeneratedOnce,
  onHasGeneratedOnceChange
}) => {
  const { show, hideAll } = useToast()
  const {
    generateCustomization,
    isGenerating,
    error,
    suggestions,
    clearError
  } = useAICustomization({ product, colors, design, currentCustomization })

  const {
    prompt,
    selectedColorIds,
    setPrompt,
    handleGenerate,
    handleReset,
    handleRetry,
    toggleColor,
    resetColorSelection
  } = useAICustomizationPanel({
    opened,
    suggestions,
    onApply,
    onClose,
    onGenerate: async (p, colorIds) => {
      // 再生成開始時に既存のトーストをすべて閉じる
      hideAll()
      await generateCustomization(p, colorIds)
    },
    onClearError: clearError,
    // 外部制御
    promptExternal,
    onPromptExternalChange,
    selectedColorIdsExternal,
    onToggleColorExternal,
    onResetSelectionExternal,
    onHasGeneratedOnceChange
  })

  // 最後に生成を実行した時点のプロンプトを保持
  const lastGeneratedPromptRef = React.useRef<string>('')
  // 最後に生成を実行した時点のカラーセットを保持
  const lastGeneratedColorIdsRef = React.useRef<Set<string>>(new Set())
  // 生成完了時に再レンダーを促すトリガ（値自体は使用しない）
  const [, bumpGenerationTick] = React.useState(0)

  // 現在の入力値の最新参照（生成完了時にのみ last*Ref へコピー）
  const promptLatestRef = React.useRef<string>('')
  React.useEffect(() => {
    promptLatestRef.current = prompt
  }, [prompt])
  const selectedColorIdsLatestRef = React.useRef<Set<string>>(new Set())
  React.useEffect(() => {
    selectedColorIdsLatestRef.current = new Set(selectedColorIds)
  }, [selectedColorIds])

  // Notify parent about generating state changes
  React.useEffect(() => {
    onGeneratingChange?.(isGenerating)
  }, [isGenerating, onGeneratingChange])

  // Show toast when AI service is unavailable (e.g., 503/rate limit)
  React.useEffect(() => {
    if (!error) return

    const status =
      (error as unknown as { status?: number }).status ??
      (error as unknown as { statusCode?: number }).statusCode

    const isServiceUnavailable =
      status === 503 ||
      status === 500 ||
      error.message === 'AI_SERVICE_UNAVAILABLE'

    if (isServiceUnavailable) {
      show({
        variant: 'error',
        title: 'AIが混み合っています',
        description:
          '現在AIの利用が集中しています。しばらく待ってから再度お試しください。',
        duration: 8000
      })
    } else {
      show({
        variant: 'error',
        title: 'AIの生成に失敗しました',
        description:
          'うまく生成できませんでした。内容を少し変えて再度お試しください。',
        duration: 8000
      })
    }
  }, [error, show])

  // 商品で使用されているマテリアルの色のみをフィルタリング
  const availableColors = React.useMemo(() => {
    const usedMaterialIds = new Set(
      design.parts.map((part) => part.material.id)
    )
    return colors.filter((color) => usedMaterialIds.has(color.material.id))
  }, [colors, design.parts])

  // In sidebar, selecting a suggestion should immediately apply to the main customization
  const handleSelectSuggestion = React.useCallback((_index: number | null) => {
    // no-op: suggestions UI removed
  }, [])

  // Sync suggestions up to parent when they change
  // suggestions flow removed

  // Sync selected suggestion index from parent to internal state
  // selected suggestion sync removed

  // Auto-apply first suggestion when generated, then close sidebar
  React.useEffect(() => {
    if (suggestions && suggestions.length > 0) {
      show({
        description: suggestions[0].reasoning,
        duration: 60000
      })
      onApply(suggestions[0].customization)

      // 計測: AI利用回数をカウント
      onTrackingAIUsage?.()

      // 計測: AI生成成功イベントをGTMへ送信
      TagManager.dataLayer({
        dataLayer: {
          event: 'aiUsedSucceeded'
        }
      })
    }
  }, [suggestions, onApply, show, onTrackingAIUsage])

  // 生成成功時点のプロンプト/カラーを記録（Dirty判定に使用）
  React.useEffect(() => {
    if (suggestions && suggestions.length > 0) {
      lastGeneratedPromptRef.current = promptLatestRef.current.trim()
      lastGeneratedColorIdsRef.current = new Set(
        selectedColorIdsLatestRef.current
      )
      bumpGenerationTick((v) => v + 1)
    }
  }, [suggestions])

  // 生成後フッターで「デザイン決定」ボタンの可視判定:
  // - 直近で suggestions が存在する もしくは 一度でも生成したことがある
  //   （生成中であっても非表示にはしない）
  const canProceed =
    (!!suggestions && suggestions.length > 0) || !!hasGeneratedOnce
  const hasGenerated = !!suggestions && suggestions.length > 0
  // プロンプトが最後に生成した内容から変化しているか
  const isPromptDirty = (() => {
    const current = prompt.trim()
    const last = (lastGeneratedPromptRef.current || '').trim()
    return current !== last
  })()
  // カラー選択が最後に生成した内容から変化しているか
  const isColorDirty = (() => {
    const last = lastGeneratedColorIdsRef.current
    if (last.size !== selectedColorIds.size) return true
    for (const id of Array.from(selectedColorIds)) {
      if (!last.has(id)) return true
    }
    return false
  })()
  // 一度でも生成したことがあるかを親へ通知（タブ戻り時のフッター制御に利用）
  React.useEffect(() => {
    if (hasGenerated) {
      onHasGeneratedOnceChange?.(true)
    }
  }, [hasGenerated, onHasGeneratedOnceChange])

  // フッター表示判定の計算
  const isInputEmpty = !prompt.trim() && selectedColorIds.size === 0
  const isInitialFooter =
    !(hasGenerated || hasGeneratedOnce) || isPromptDirty || isColorDirty

  return (
    <Container>
      <Sections>
        <ScrollableContent>
          <PromptInputSection
            prompt={prompt}
            onPromptChange={setPrompt}
            onGenerate={handleGenerate}
            onReset={handleReset}
            onRetry={handleRetry}
            isGenerating={isGenerating}
            error={error}
            hasColorSelection={selectedColorIds.size > 0}
            selectedColorIds={selectedColorIds}
            availableColors={availableColors}
            showInlineActions={false}
          />

          <ColorSelectionSection
            availableColors={availableColors}
            selectedColorIds={selectedColorIds}
            onToggleColor={toggleColor}
            onResetSelection={resetColorSelection}
          />

          {null}

          {null}
        </ScrollableContent>

        {/* Suggestions are rendered under the main preview by the parent page */}
        <AICustomizationSidebarFooter
          isInitialFooter={isInitialFooter}
          isGenerating={isGenerating}
          hasError={!!error}
          isInputEmpty={isInputEmpty}
          onGenerate={handleGenerate}
          onRetry={handleRetry}
          onReset={handleReset}
          onClose={onClose}
          canProceed={canProceed}
          onProceedToEngraving={onProceedToEngraving}
        />
      </Sections>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

const Sections = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0; /* outer container provides padding */
  display: flex;
  flex-direction: column;
  background: var(--white);
`

const ScrollableContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-height: 188px;
  }
`

// Footer系のstyledはサブコンポーネントへ移動
